import { useAuth } from '../auth/useAuth'

/* ─── MCP surface gating (MS-700 AC-1) ───
   The single predicate behind BOTH MCP surfaces — MCP Config (/mambu-mcp) and
   MCP Analytics (/mcp-analytics). Their specs require identical gating
   ("Gated identically to MCP Config", MS-700 AC-1), so they share one
   definition rather than two copies of `isGlobalAdmin &&` that can drift apart.

   IMPORTANT — this is a UX gate, not a security boundary. It exists so a
   non-admin is not sent to a page that will only 403 on its first fetch. The
   real enforcement is server-side: every endpoint these pages call runs
   `require_global_permission(level="Admin")` (see
   src/studio/api/v1/mambu_connections.py and .../global_settings.py). Do not
   weaken a backend check on the strength of this hook. */

/**
 * Whether the tenant holds the Mambu MCP subscription.
 *
 * PLACEHOLDER — hardcoded `true` until the real signal exists.
 *
 * The authoritative subscription state will come from the **billing engine**,
 * which is still being built. It is deliberately NOT derived from
 * `_platform.tenants` (`tier`, `mcp_scenario`, `seat_count`, `trial_ends_at`):
 * those columns originate in old mockups and sample data, are not the source of
 * truth, and may not survive — do not wire this to them.
 *
 * Kept as a source constant rather than an env var on purpose: it is a
 * placeholder awaiting an upstream system, not an operator-tunable flag, and
 * making it configurable would invite someone to switch it off in an
 * environment where it carries no meaning.
 *
 * When the billing engine lands, replace this constant with the real value and
 * every call site below follows automatically.
 */
const HAS_MCP_SUBSCRIPTION = true

export interface McpSurfaceAccess {
  /** True when the caller may see the MCP Config and MCP Analytics surfaces. */
  canSeeMcpSurfaces: boolean
}

export function useMcpSurfaceAccess(): McpSurfaceAccess {
  const { isGlobalAdmin } = useAuth()
  return { canSeeMcpSurfaces: isGlobalAdmin && HAS_MCP_SUBSCRIPTION }
}
